import React, { useMemo, useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { formatGrade } from '@utils/gradeUtils.js';
import { sortChronologicallyCleanSendsLast } from '@utils/attemptUtils.js';
import { formatAscentDate } from '@utils/dateUtils.js';

const ProjectTracker = ({ data, climbingStyle, gradeSystem }) => {
  const [sortField, setSortField] = useState('distinctDays');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showOpenOnly, setShowOpenOnly] = useState(true);

  // Define the default sort priority
  const defaultSortPriority = ['distinctDays', 'attempts', 'days'];

  const projectData = useMemo(() => {
    if (!data?.length) return [];

    // Group attempts by route using Route ID
    const routeAttempts = data.reduce((acc, attempt) => {
      const routeKey = attempt['Route ID'];
      if (!acc[routeKey]) {
        acc[routeKey] = [];
      }
      acc[routeKey].push(attempt);
      return acc;
    }, {});

    // Process each route's attempts
    const projects = Object.entries(routeAttempts).map(([routeId, attempts]) => {
      // Sort attempts chronologically with clean sends last on same day
      const sortedAttempts = sortChronologicallyCleanSendsLast(attempts);

      const firstCleanSend = sortedAttempts.find(a => a.isClean);
      const firstSendIndex = firstCleanSend ? sortedAttempts.indexOf(firstCleanSend) : -1;

      const projectingAttempts = firstSendIndex !== -1 ?
        sortedAttempts.slice(0, firstSendIndex + 1) :
        sortedAttempts;

      const postSendAttempts = firstSendIndex !== -1 ?
        sortedAttempts.slice(firstSendIndex + 1) :
        [];

      const repeats = postSendAttempts.filter(a => a.isClean).length;
      const unsends = postSendAttempts.filter(a => !a.isClean).length;
      
      const distinctProjectDays = new Set(
        projectingAttempts.map(a => a['Ascent Date'])
      ).size;

      const totalProjectAttempts = projectingAttempts.length;
      const daysToSend = firstCleanSend ? Math.ceil(
        (new Date(firstCleanSend['Ascent Date']).getTime() - new Date(sortedAttempts[0]['Ascent Date']).getTime()) /
        (1000 * 60 * 60 * 24)
      ) + 1 : Math.ceil(
        (new Date(sortedAttempts[sortedAttempts.length - 1]['Ascent Date']).getTime() - new Date(sortedAttempts[0]['Ascent Date']).getTime()) /
        (1000 * 60 * 60 * 24)
      ) + 1;

      return {
        routeId: routeId,
        routeName: sortedAttempts[0]['Route Name'],
        cragName: sortedAttempts[0]['Crag Name'],
        grade: sortedAttempts[0]['Route Grade'],
        attempts: totalProjectAttempts,
        distinctDays: distinctProjectDays,
        days: daysToSend,
        status: firstCleanSend ? 'Sent' : 'Open Project',
        repeats: repeats,
        unsends: unsends,
        firstDate: sortedAttempts[0]['Ascent Date'],
        lastProjectDate: projectingAttempts[projectingAttempts.length - 1]['Ascent Date'],
        sendDate: firstCleanSend?.['Ascent Date']
      };
    });

    // Filter to only include routes with multiple sessions
    const multiSessionProjects = projects.filter(p => p.distinctDays > 1);

    // Filter for open projects if showOpenOnly is true
    const filteredProjects = showOpenOnly ? 
      multiSessionProjects.filter(p => p.status === 'Open Project') : 
      multiSessionProjects;

    // Create sort priority list based on current sort field
    const sortPriority = [
      sortField,
      ...defaultSortPriority.filter(field => field !== sortField)
    ];

    // Sort projects using priority system
    return filteredProjects.sort((a, b) => {
      // Iterate through sort priority fields
      for (const field of sortPriority) {
        if (a[field] !== b[field]) {
          return (sortDirection === 'desc' ? -1 : 1) * (a[field] - b[field]);
        }
      }
      return 0; // If all priority fields are equal
    });
  }, [data, sortField, sortDirection, showOpenOnly]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'desc' ? 
      <ChevronDownIcon className="inline w-4 h-4" /> : 
      <ChevronUpIcon className="inline w-4 h-4" />;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={showOpenOnly}
              onChange={(e) => setShowOpenOnly(e.target.checked)}
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-900">
              Show open projects only
            </span>
          </label>
        </div>
      </div>
      <div className="overflow-auto flex-1">
        <table className="min-w-full table-auto">
          <thead className="sticky top-0 bg-white shadow-sm">
            <tr className="bg-gray-50">
              <th className="px-4 py-2 text-left">Route</th>
              <th className="px-4 py-2 text-left">Grade</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('attempts')}>
                Attempts <SortIcon field="attempts" />
              </th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('distinctDays')}>
                Sessions <SortIcon field="distinctDays" />
              </th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('days')}>
                Days <SortIcon field="days" />
              </th>
              <th className="px-4 py-2">Post-Send</th>
              <th className="px-4 py-2">Date Range</th>
            </tr>
          </thead>
<tbody>
  {projectData.map((project, index) => (
    <tr 
      key={project.routeId}
      className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
    >
      <td className="px-4 py-2">
        <div className="font-medium">{project.routeName}</div>
        <div className="text-sm text-gray-500">{project.cragName}</div>
      </td>
      <td className="px-4 py-2">
        {formatGrade(project.grade, gradeSystem, climbingStyle)}
      </td>
      <td className="px-4 py-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          project.status === 'Sent' ? 
          'bg-green-100 text-green-800' : 
          'bg-yellow-100 text-yellow-800'
        }`}>
          {project.status}
        </span>
      </td>
      <td className="px-4 py-2">{project.attempts}</td>
      <td className="px-4 py-2">{project.distinctDays}</td>
      <td className="px-4 py-2">{project.days}</td>
      <td className="px-4 py-2">
        {project.status === 'Sent' && (project.repeats > 0 || project.unsends > 0) ? (
          <span className="text-sm">
            {project.repeats > 0 ? `${project.repeats} repeat${project.repeats > 1 ? 's' : ''}` : ''}
            {project.repeats > 0 && project.unsends > 0 ? ', ' : ''}
            {project.unsends > 0 ? `${project.unsends} unsend${project.unsends > 1 ? 's' : ''}` : ''}
          </span>
        ) : '-'}
      </td>
      <td className="px-4 py-2 text-sm">
        {formatAscentDate(project.firstDate)} - {formatAscentDate(project.lastProjectDate)}
      </td>
    </tr>
  ))}
</tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectTracker;